// src/pages/Home.js
import React, { useEffect, useState } from 'react';
import {
  Box, Flex, Heading, Text, SimpleGrid, Card, CardBody, Button, HStack, VStack, Badge,
  Container, Image, Link, useColorModeValue,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import {
  BookOpen, Headphones, Library, Brain, Upload, ThumbsUp, ShieldCheck,
  ArrowRight, LayoutDashboard, Layers,
} from 'lucide-react';
import Footer from '../components/Layout/footer';
import { useAuth } from '../context/AuthContext';
import { getResources } from '../api/resourceService';
import { getModels } from '../api/modelService';
import useResourceOptions from '../hooks/useResourceOptions';
import { EndorsementCounts, typeStyle } from '../components/Resources/ResourceBadges';

const NAV_LINKS = [
  { label: 'Resources', to: '/resources' },
  { label: 'AI Models', to: '/models' },
  { label: 'Read Quran', to: '/surahs' },
  { label: 'About', to: '/about' },
];

// Who can use each feature
const AUDIENCES = {
  everyone: { label: 'Open to everyone', color: 'green' },
  account: { label: 'Free account', color: 'blue' },
  expert: { label: 'Experts', color: 'purple' },
};

const FEATURES = [
  {
    icon: BookOpen, title: 'Read the Quran', audience: 'everyone', to: '/surahs',
    description: 'All 114 surahs with translations, by surah, juz or mushaf page.',
  },
  {
    icon: Headphones, title: 'Listen to recitations', audience: 'everyone', to: '/surahs',
    description: 'Play any ayah or a whole surah, follow along as it is recited, and download the audio.',
  },
  {
    icon: Library, title: 'Expert-endorsed resources', audience: 'everyone', to: '/resources',
    description: 'Datasets, documents, recitations and links on the Quran, Tafseer, Hadith and more, previewed right in the page.',
  },
  {
    icon: Brain, title: 'Quranic AI models', audience: 'everyone', to: '/models',
    description: 'Download models that recognise surahs, ayahs and reciters. Log in to try them on your own audio.',
  },
  {
    icon: Upload, title: 'Share your work', audience: 'account', to: '/contribute',
    description: 'Upload a dataset, document or recording, or link to a useful site, and explain how people can use it.',
  },
  {
    icon: ThumbsUp, title: 'Endorse resources', audience: 'expert', to: () => '/resources?view=review',
    description: 'Experts vote Endorse or Not endorse on each submission, with comments others can read.',
  },
  {
    icon: ShieldCheck, title: 'Verify Quranic data', audience: 'expert',
    to: (role) => (role === 'super_expert' ? '/verifier' : '/expert'),
    description: 'Experts and verifiers check ayah text, translations and recitation audio.',
  },
];

const REVIEWER_ROLES = ['admin', 'expert', 'super_expert'];

const formatCount = (n) => (n == null ? '–' : n.toLocaleString());

// Expert features link to the reviewer pages only for reviewers; for others the card just explains them
const featureLink = (feature, role) => {
  if (typeof feature.to !== 'function') return feature.to;
  return REVIEWER_ROLES.includes(role) ? feature.to(role) : null;
};


// ============ PIECES ============

const TopNav = ({ user }) => {
  const bg = useColorModeValue('whiteAlpha.900', 'gray.900');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  return (
    <Box as="header" position="sticky" top={0} zIndex={10} bg={bg} backdropFilter="blur(8px)" borderBottom="1px" borderColor={borderColor}>
      <Container maxW="7xl" py={3}>
        <Flex justify="space-between" align="center" gap={4} wrap="wrap">
          <HStack as={RouterLink} to="/" spacing={3} color="teal.700" minW={0}>
            <Image src={`${process.env.PUBLIC_URL}/brand/cqr-ai-hub-logo.jpg`} alt="CQR AI Hub" boxSize="48px" objectFit="contain" flexShrink={0} />
            <Heading fontSize={{ base: 'md', md: 'lg' }}>CQR Quranic AI Datahub</Heading>
          </HStack>
          <HStack spacing={6} display={{ base: 'none', md: 'flex' }}>
            {NAV_LINKS.map((l) => (
              <Link key={l.to} as={RouterLink} to={l.to} fontWeight="medium" _hover={{ color: 'teal.500' }}>
                {l.label}
              </Link>
            ))}
          </HStack>
          <HStack spacing={2}>
            {user ? (
              <Button as={RouterLink} to="/dashboard" colorScheme="teal" size="sm" leftIcon={<LayoutDashboard size={16} />}>
                Dashboard
              </Button>
            ) : (
              <>
                <Button as={RouterLink} to="/login" variant="ghost" size="sm">Log in</Button>
                <Button as={RouterLink} to="/register" colorScheme="teal" size="sm">Sign up</Button>
              </>
            )}
          </HStack>
        </Flex>
      </Container>
    </Box>
  );
};

const HeroStat = ({ value, label }) => (
  <Box bg="whiteAlpha.200" border="1px solid" borderColor="whiteAlpha.300" borderRadius="xl" p={4} backdropFilter="blur(6px)">
    <Text fontSize="3xl" fontWeight="bold" lineHeight={1.1}>{value}</Text>
    <Text fontSize="sm" opacity={0.85}>{label}</Text>
  </Box>
);

const FeatureCard = ({ feature, to }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const subtextColor = useColorModeValue('gray.600', 'gray.400');
  const iconBg = useColorModeValue('teal.50', 'teal.900');
  const Icon = feature.icon;
  const audience = AUDIENCES[feature.audience];
  return (
    <Card
      {...(to ? { as: RouterLink, to } : {})}
      bg={cardBg} variant="outline" borderColor={borderColor} borderRadius="xl" h="100%"
      transition="all 0.2s"
      _hover={to ? { transform: 'translateY(-4px)', boxShadow: 'lg', borderColor: 'teal.300' } : undefined}
    >
      <CardBody>
        <Flex justify="space-between" align="flex-start" mb={4}>
          <Flex w="44px" h="44px" borderRadius="lg" bg={iconBg} color="teal.500" align="center" justify="center">
            <Icon size={22} />
          </Flex>
          <Badge colorScheme={audience.color} variant="subtle" fontSize="2xs">{audience.label}</Badge>
        </Flex>
        <Heading size="sm" mb={2}>{feature.title}</Heading>
        <Text fontSize="sm" color={subtextColor}>{feature.description}</Text>
      </CardBody>
    </Card>
  );
};

const Step = ({ number, title, children }) => {
  const subtextColor = useColorModeValue('gray.600', 'gray.400');
  return (
    <VStack align="start" spacing={2} flex={1}>
      <Flex w="36px" h="36px" borderRadius="full" bg="teal.500" color="white" align="center" justify="center" fontWeight="bold">
        {number}
      </Flex>
      <Heading size="sm">{title}</Heading>
      <Text fontSize="sm" color={subtextColor}>{children}</Text>
    </VStack>
  );
};

const RecentResource = ({ resource, options }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const { icon: Icon, color } = typeStyle(resource.resource_type);
  return (
    <Card
      as={RouterLink} to={`/resources/${resource.id}`}
      bg={cardBg} variant="outline" borderColor={borderColor} borderRadius="xl"
      transition="all 0.2s" _hover={{ boxShadow: 'md', borderColor: `${color}.300` }}
    >
      <CardBody>
        <HStack spacing={3} mb={2}>
          <Box color={`${color}.500`}><Icon size={18} /></Box>
          <Text fontSize="xs" color="gray.500">{options.resourceType(resource.resource_type)?.label}</Text>
        </HStack>
        <Heading size="sm" noOfLines={2} mb={3}>{resource.title}</Heading>
        <Flex justify="space-between" align="center">
          <Badge variant="subtle" fontSize="2xs">{options.knowledgeLabel(resource.knowledge_type)}</Badge>
          <EndorsementCounts resource={resource} />
        </Flex>
      </CardBody>
    </Card>
  );
};


// ============ PAGE ============

const Home = () => {
  const { user } = useAuth();
  const options = useResourceOptions();
  const [stats, setStats] = useState({ resources: null, models: null });
  const [recent, setRecent] = useState([]);

  const heroBg = useColorModeValue(
    'linear-gradient(135deg, #319795 0%, #2c5282 100%)',
    'linear-gradient(135deg, #234e52 0%, #1a202c 100%)'
  );
  const pageBg = useColorModeValue('gray.50', 'gray.900');
  const sectionBg = useColorModeValue('white', 'gray.800');
  const subtextColor = useColorModeValue('gray.600', 'gray.400');

  useEffect(() => {
    // Counts and the latest resources are a nice extra; the page works without them
    getResources({ status: 'published', sort: 'top', page_size: 4 })
      .then((data) => {
        setStats((s) => ({ ...s, resources: data.total }));
        setRecent(data.resources || []);
      })
      .catch(() => {});
    getModels({ status: 'approved', page_size: 1 })
      .then((data) => setStats((s) => ({ ...s, models: data.total })))
      .catch(() => {});
  }, []);

  return (
    <Box bg={pageBg} minH="100vh" display="flex" flexDirection="column">
      <TopNav user={user} />

      {/* Hero */}
      <Box bg={heroBg} color="white" position="relative" overflow="hidden">
        <Box position="absolute" right="-60px" top="-40px" opacity={0.07}>
          <Layers size={420} />
        </Box>
        <Container maxW="7xl" py={{ base: 14, md: 20 }} position="relative">
          <Flex direction={{ base: 'column', lg: 'row' }} gap={12} align={{ lg: 'center' }}>
            <Box flex={3}>
              <Badge bg="whiteAlpha.300" color="white" mb={4} px={3} py={1} borderRadius="full" textTransform="none">
                Quranic data for learning, research and AI
              </Badge>
              <Heading size={{ base: '2xl', md: '3xl' }} lineHeight="shorter" mb={5}>
                Read, share and build on expert-verified Quranic data
              </Heading>
              <Text fontSize={{ base: 'md', md: 'lg' }} opacity={0.9} maxW="600px" mb={8}>
                Read and listen to the Quran with translations, find datasets and documents endorsed by
                experts, and try AI models made for the Quran. Browsing is free, with no account needed.
              </Text>
              <HStack spacing={3} flexWrap="wrap" rowGap={3}>
                <Button as={RouterLink} to="/resources" size="lg" bg="white" color="teal.700" _hover={{ bg: 'gray.100' }} rightIcon={<ArrowRight size={18} />}>
                  Explore resources
                </Button>
                <Button as={RouterLink} to="/models" size="lg" variant="outline" color="white" borderColor="whiteAlpha.600" _hover={{ bg: 'whiteAlpha.200' }}>
                  Browse AI models
                </Button>
              </HStack>
              <Link as={RouterLink} to="/dashboard" display="inline-flex" alignItems="center" gap={1} mt={5} fontSize="sm" opacity={0.9} _hover={{ opacity: 1 }}>
                {user ? 'Go to your dashboard' : 'Or open the dashboard as a guest'} <ArrowRight size={14} />
              </Link>
            </Box>
            <SimpleGrid flex={2} columns={2} spacing={4}>
              <HeroStat value="114" label="Surahs" />
              <HeroStat value="6,236" label="Ayahs" />
              <HeroStat value={formatCount(stats.resources)} label="Published resources" />
              <HeroStat value={formatCount(stats.models)} label="AI models" />
            </SimpleGrid>
          </Flex>
        </Container>
      </Box>

      {/* What you can do */}
      <Container maxW="7xl" py={16}>
        <Box textAlign="center" mb={10}>
          <Heading size="xl" mb={3}>What you can do here</Heading>
          <Text color={subtextColor} maxW="640px" mx="auto">
            Everything marked "Open to everyone" works without logging in. Create a free account to share
            your own work and try the AI models.
          </Text>
        </Box>
        <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={5}>
          {FEATURES.map((f) => <FeatureCard key={f.title} feature={f} to={featureLink(f, user?.role)} />)}
        </SimpleGrid>
      </Container>

      {/* How publishing works */}
      <Box bg={sectionBg} py={16}>
        <Container maxW="7xl">
          <Heading size="lg" mb={2}>How a resource gets published</Heading>
          <Text color={subtextColor} mb={10}>Every shared resource is checked by experts before it appears for everyone.</Text>
          <Flex direction={{ base: 'column', md: 'row' }} gap={10}>
            <Step number={1} title="Share it">
              Choose what it is (dataset, document, audio, link…) and what it is about (Quran, Tafseer,
              Hadith…), explain how people can use it, then upload the file or paste the link.
            </Step>
            <Step number={2} title="Experts vote">
              Experts endorse it or not, like an upvote and downvote, and explain their concerns.
              Everyone can see the votes.
            </Step>
            <Step number={3} title="Published for everyone">
              It is published automatically once {options.endorsementRule}. Anyone can then preview,
              download or open it.
            </Step>
          </Flex>
        </Container>
      </Box>

      {/* Recently endorsed */}
      {recent.length > 0 && (
        <Container maxW="7xl" py={16}>
          <Flex justify="space-between" align="center" mb={6} gap={4} wrap="wrap">
            <Heading size="lg">Most endorsed resources</Heading>
            <Button as={RouterLink} to="/resources" variant="link" colorScheme="teal" rightIcon={<ArrowRight size={16} />}>
              See all
            </Button>
          </Flex>
          <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={5}>
            {recent.map((r) => <RecentResource key={r.id} resource={r} options={options} />)}
          </SimpleGrid>
        </Container>
      )}

      {/* Call to action */}
      <Container maxW="7xl" pb={16} pt={recent.length > 0 ? 0 : 16}>
        <Flex
          bg={heroBg} color="white" borderRadius="2xl" p={{ base: 8, md: 12 }}
          direction={{ base: 'column', md: 'row' }} justify="space-between" align={{ md: 'center' }} gap={6}
        >
          <Box>
            <Heading size="lg" mb={2}>{user ? 'Pick up where you left off' : 'Have Quranic data to share?'}</Heading>
            <Text opacity={0.9}>
              {user
                ? 'Your submissions, reviews and models are on your dashboard.'
                : 'Sign up for free to share resources and models, and help build trusted Quranic data.'}
            </Text>
          </Box>
          <HStack spacing={3} flexShrink={0}>
            {user ? (
              <Button as={RouterLink} to="/dashboard" size="lg" bg="white" color="teal.700" _hover={{ bg: 'gray.100' }}>
                Open dashboard
              </Button>
            ) : (
              <>
                <Button as={RouterLink} to="/register" size="lg" bg="white" color="teal.700" _hover={{ bg: 'gray.100' }}>
                  Sign up
                </Button>
                <Button as={RouterLink} to="/login" size="lg" variant="outline" color="white" borderColor="whiteAlpha.600" _hover={{ bg: 'whiteAlpha.200' }}>
                  Log in
                </Button>
              </>
            )}
          </HStack>
        </Flex>
      </Container>

      <Box mt="auto"><Footer /></Box>
    </Box>
  );
};

export default Home;
